<template>
   <div class="container-fluid playground bg-light-purple">
      <div v-if="playgroundData && playgroundData.code" class="content">
         <div class="row">
            <div class="container-fluid playground-header">
               <div class="row px-15 px-sm-0 mt-2">
                  <div class="col-12 title">
                     <h1>{{ playgroundData.header }}</h1>
                     <span class="badge badge-primary">Part of {{ playgroundData.product }}</span>
                  </div>
               </div>
            </div>
         </div>
         <div class="row">
            <div class="col-12 show-chart">
               <ChartView
                  :html-code="playgroundData.code.htmlCode"
                  :js-code="playgroundData.code.jsCode"
                  :css-code="playgroundData.code.cssCode"
                  :seo-img-url="playgroundData.preview"
                  :seo-img-alt="playgroundData.header"
                  :show-themes="false"
               />
               <h2 v-if="playgroundData.shortDescription" class="mt-2 mb-2">
                  {{ descriptionJoiner(playgroundData.shortDescription) }}
               </h2>

               <a v-if="prevLink" :href="prevLink" class="prev-slide-btn" @click.prevent="goPrev()">
                  <i class="fc_keyboard_arrow_left" />
               </a>
               <a v-if="nextLink" :href="nextLink" class="next-slide-btn" @click.prevent="goNext()">
                  <i class="fc_keyboard_arrow_right" />
               </a>
            </div>
         </div>
         <div v-if="isClient" class="row">
            <mq-layout class="code-view-wrapper col-12" :mq="['xs', 'sm', 'md']">
               <CodeViewSm
                  :display="playgroundData.code.display"
                  :slug="playgroundData.slug"
                  :is-static="playgroundData.isStatic"
               />
            </mq-layout>
            <mq-layout class="code-view-wrapper col-12" mq="lg+">
               <CodeView
                  :display="playgroundData.code.display"
                  :slug="playgroundData.slug"
                  :is-static="playgroundData.isStatic"
                  :code-details="codeViewDetails"
               />
            </mq-layout>
         </div>
      </div>
   </div>
</template>

<script>
import { mapState } from 'vuex';

import metaGenerator from '../../util/meta.js';
import languages from '../../components/playground/languages.js';

export default {
   components: {
      ChartView: () =>
         import(/* webpackChunkName: "playground" */ '../../components/playground/ChartView'),
      CodeView: () =>
         import(/* webpackChunkName: "playground" */ '../../components/playground/CodeView'),
      CodeViewSm: () =>
         import(/* webpackChunkName: "playground" */ '../../components/playground/CodeViewSm'),
   },

   data() {
      return {
         languages,
         isClient: false,
         nextLink: null,
         prevLink: null,
      };
   },

   computed: {
      ...mapState(['playgroundData', 'playgroundSidebarData']),
      codeViewDetails() {
         return {
            title: this.playgroundData.meta.header,
            description: this.playgroundData.meta.description,
         };
      },
   },

   asyncData({ store, route }) {
      if (typeof window !== 'undefined') {
         return Promise.all([
            store.dispatch('MAPS_PAGE_DATA_FETCH', {
               type: route.params.type,
               name: route.params.name,
               language: store.state.fcUserSelectedFramework,
            }),
         ]);
      }

      return Promise.all([
         store.dispatch('MAPS_PAGE_DATA_FETCH', {
            type: route.params.type,
            name: route.params.name,
         }),
      ]);
   },

   beforeRouteUpdate(to, from, next) {
      next();
      this.initNavLinks();
   },

   created() {
      if (!this.playgroundData || (this.playgroundData && !this.playgroundData.code)) {
         throw Object.assign(new Error(), {
            code: 404,
            message: 'Map Details NOT FOUND',
         });
      }

      this.initNavLinks();
   },

   beforeMount() {
      const frameworks = this.languages.map(el => el.value);
      const lang =
         frameworks.indexOf(this.$getUserSelectedFramework()) > -1
            ? this.$getUserSelectedFramework()
            : 'js';
      this.$set(this.$store.state, 'fcUserSelectedFramework', lang);
   },

   mounted() {
      this.isClient = true;
   },

   metaInfo() {
      return metaGenerator(this.playgroundData);
   },

   methods: {
      initNavLinks() {
         this.nextLink = null;
         this.prevLink = null;

         if (!this.playgroundSidebarData) return;

         const currentPath = this.$route.path;
         let hitItem = false;

         function extractLinks({ items }, parentItems, rootIdx) {
            if (!items) return false;
            if (hitItem) return true;
            let prevLink;
            let nextLink;

            for (let idx = 0; idx < items.length; idx += 1) {
               const subItem = items[idx];
               if (subItem.link === currentPath) {
                  if (idx > 0) {
                     prevLink = items[idx - 1].link;
                  } else if (rootIdx > 0) {
                     const prevList = parentItems[rootIdx - 1].items;
                     if (prevList && prevList.length > 0) {
                        prevLink = prevList[prevList.length - 1].link;
                     }
                  }

                  if (idx < items.length - 1) {
                     nextLink = items[idx + 1].link;
                  } else if (rootIdx < parentItems.length - 1) {
                     const nextList = parentItems[rootIdx + 1].items;
                     if (nextList && nextList.length > 0) {
                        nextLink = nextList[0].link;
                     }
                  }

                  hitItem = true;
                  break;
               }

               if (subItem.items && subItem.items.length > 0) {
                  if (!hitItem) {
                     [prevLink, nextLink] = extractLinks(subItem, items, idx);
                  } else break;
               }
            }

            return [prevLink, nextLink];
         }

         const chartGalleryItems = this.playgroundSidebarData[0].items;
         for (let index = 0; index < chartGalleryItems.length; index += 1) {
            const subItem = chartGalleryItems[index];
            if (!hitItem) {
               [this.prevLink, this.nextLink] = extractLinks(subItem, chartGalleryItems, index);
            } else break;
         }
      },

      goNext() {
         this.$bar.start();
         this.$router.push(this.nextLink, this.$bar.finish);
      },
      goPrev() {
         this.$bar.start();
         this.$router.push(this.prevLink, this.$bar.finish);
      },
   },
};
</script>

<style lang="scss">
@import '../../util';

.playground {
   width: 100%;
   overflow: hidden;

   .show-chart {
      .next-slide-btn,
      .prev-slide-btn {
         position: absolute;
         top: 40%;
         display: flex;
         padding: 10px;
         background-color: #fff;
         color: #000;
         box-shadow: 0 4px 9px 0 rgba(23, 28, 33, 0.12);
         border-radius: 50%;
         transform: scale(0.9);
         text-decoration: none;

         &:hover {
            text-decoration: none;
         }

         @include media-breakpoint-up(sm) {
            transform: scale(1);
         }

         i {
            position: relative;
            top: 1px;
         }
      }
      .next-slide-btn {
         right: -11px;

         @include media-breakpoint-up(sm) {
            right: -15px;
         }
      }

      .prev-slide-btn {
         left: -11px;

         @include media-breakpoint-up(sm) {
            left: -15px;
         }
      }
   }

   .code-view-wrapper {
      width: 100%;
   }
}

.playground-header {
   margin-bottom: 32px;

   .title {
      display: flex;
      flex-flow: column nowrap;
      align-items: flex-start;

      @include media-breakpoint-up(sm) {
         flex-flow: row nowrap;
         align-items: center;
      }

      h2,
      h1 {
         color: #212344;
         font-size: 30px;
         line-height: 37px;
      }

      span {
         font-size: 12px;

         letter-spacing: 0.2px;
         line-height: 15px;
         color: #212344;
         border: 1px solid rgba(62, 57, 107, 0.13);
         border-radius: 2px;
         background-color: rgba(62, 57, 107, 0.04);

         @include media-breakpoint-up(sm) {
            margin-left: 10px;
         }
      }
   }

   .description {
      color: #313131;
      font-size: 16px;
      letter-spacing: 0.2px;
      line-height: 28px;
      overflow: hidden;
      max-height: 100px;
      will-change: max-height;
      transition: 0.8s max-height ease-in-out;
      padding-top: 15px;
   }

   .btn-read-more {
      display: inline-block;
      color: #8297a2;
      text-transform: uppercase;
      cursor: pointer;

      &:hover,
      &:active {
         color: darken(#8297a2, 15%);
      }
   }
}

.content {
   padding: 30px 0;

   @include media-breakpoint-up(sm) {
      padding: 30px;
   }

   @include media-breakpoint-up(md) {
      padding: 50px 30px 30px 30px;
   }

   @include media-breakpoint-up(lg) {
      padding: 50px 30px 50px 30px;
   }
}
</style>
